import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation, useInView, AnimatePresence } from 'framer-motion';
import * as Icons from 'react-icons/fa';
import { FiMail, FiPhone, FiGlobe, FiLinkedin, FiGithub, FiTwitter } from 'react-icons/fi';
import { HiOutlineDocumentText, HiOutlineCode, HiOutlinePencilAlt, HiOutlineChartBar } from 'react-icons/hi';
import { BsStarFill, BsCheckCircle, BsClock, BsArrowRight } from 'react-icons/bs';
import { IoMdTime } from 'react-icons/io';

// Add Tailwind CSS via CDN
const tailwindScript = document.createElement('script');
tailwindScript.src = 'https://cdn.tailwindcss.com';
document.head.appendChild(tailwindScript);

// Load premium fonts
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Cutive+Mono&family=Inter:wght@300;400;500;600;700;800;900&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);

// Configure Tailwind with custom theme
tailwindScript.onload = () => {
  (window as any).tailwind.config = {
    theme: {
      extend: {
        fontFamily: {
          'sans': ['Inter', 'sans-serif'],
          'mono': ['Cutive Mono', 'monospace'],
          'display': ['Cutive Mono', 'monospace'],
        },
        animation: {
          'scroll': 'scroll 20s linear infinite',
          'marquee': 'marquee 30s linear infinite',
        },
        keyframes: {
          scroll: {
            '0%': { transform: 'translateX(0)' },
            '100%': { transform: 'translateX(-50%)' }
          },
          marquee: {
            '0%': { transform: 'translateX(0%)' },
            '100%': { transform: 'translateX(-50%)' }
          }
        }
      }
    }
  };
  
  // Add custom styles
  const customStyles = document.createElement('style');
  customStyles.textContent = `
    /* Hide scrollbar for Chrome, Safari and Opera */
    .no-scrollbar::-webkit-scrollbar {
      display: none;
    }
    
    /* Hide scrollbar for IE, Edge and Firefox */
    .no-scrollbar {
      -ms-overflow-style: none;  /* IE and Edge */
      scrollbar-width: none;  /* Firefox */
    }
    
    /* Line clamp for multiline text truncation */
    .line-clamp-3 {
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  `;
  document.head.appendChild(customStyles);
};

// Professional Design System
const theme = {
  colors: {
    primary: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#e5e5e5',
      300: '#d4d4d4',
      400: '#a3a3a3',
      500: '#737373',
      600: '#525252',
      700: '#404040',
      800: '#262626',
      900: '#171717',
      950: '#0a0a0a',
    },
    yellow: {
      50: '#fefce8',
      100: '#fef3c7',
      200: '#fde68a',
      300: '#fcd34d',
      400: '#fbbf24',
      500: '#f59e0b',
      600: '#d97706',
      700: '#b45309',
      800: '#92400e',
      900: '#78350f',
    },
    accent: {
      50: '#fef2f2',
      100: '#fee2e2',
      200: '#fecaca',
      300: '#fca5a5',
      400: '#f87171',
      500: '#ef4444',
      600: '#dc2626',
      700: '#b91c1c',
      800: '#991b1b',
      900: '#7f1d1d',
    },
    blue: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
    },
    emerald: {
      50: '#ecfdf5',
      100: '#d1fae5',
      200: '#a7f3d0',
      300: '#6ee7b7',
      400: '#34d399',
      500: '#10b981',
      600: '#059669',
      700: '#047857',
      800: '#065f46',
      900: '#064e3b',
    }
  },
  typography: {
    fontFamily: {
      sans: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      display: '"Cutive Mono", monospace',
      mono: '"Cutive Mono", monospace',
    },
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem',
      '5xl': '3rem',
      '6xl': '3.75rem',
      '7xl': '4.5rem',
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
    lineHeight: {
      tight: 1.25,
      snug: 1.375,
      normal: 1.5,
      relaxed: 1.625,
      loose: 2,
    }
  },
  spacing: {
    px: '1px',
    0: '0',
    1: '0.25rem',
    2: '0.5rem',
    3: '0.75rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    8: '2rem',
    10: '2.5rem',
    12: '3rem',
    16: '4rem',
    20: '5rem',
    24: '6rem',
    32: '8rem',
    40: '10rem',
    48: '12rem',
    56: '14rem',
    64: '16rem',
  },
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    base: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    '2xl': '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  },
  borderRadius: {
    none: '0',
    sm: '0.125rem',
    base: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
    '3xl': '1.5rem',
    full: '9999px',
  }
};

// Modern Component Styles
const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: `0 ${theme.spacing[6]}`,
    '@media (max-width: 768px)': {
      padding: `0 ${theme.spacing[4]}`,
    }
  },
  section: {
    padding: `${theme.spacing[20]} 0`,
    '@media (max-width: 768px)': {
      padding: `${theme.spacing[16]} 0`,
    }
  },
  button: {
    primary: {
      background: `linear-gradient(135deg, ${theme.colors.blue[600]} 0%, ${theme.colors.blue[700]} 100%)`,
      color: 'white',
      padding: `${theme.spacing[3]} ${theme.spacing[8]}`,
      borderRadius: theme.borderRadius.xl,
      border: 'none',
      fontSize: theme.typography.fontSize.base,
      fontWeight: theme.typography.fontWeight.medium,
      fontFamily: theme.typography.fontFamily.sans,
      cursor: 'pointer',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: theme.shadows.md,
    },
    secondary: {
      background: 'transparent',
      color: theme.colors.primary[700],
      padding: `${theme.spacing[3]} ${theme.spacing[8]}`,
      borderRadius: theme.borderRadius.xl,
      border: `2px solid ${theme.colors.primary[200]}`,
      fontSize: theme.typography.fontSize.base,
      fontWeight: theme.typography.fontWeight.medium,
      fontFamily: theme.typography.fontFamily.sans,
      cursor: 'pointer',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    ghost: {
      background: 'transparent',
      color: theme.colors.primary[600],
      padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
      borderRadius: theme.borderRadius.lg,
      border: 'none',
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
      fontFamily: theme.typography.fontFamily.sans,
      cursor: 'pointer',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    }
  },
  card: {
    base: {
      background: 'white',
      borderRadius: theme.borderRadius['2xl'],
      boxShadow: theme.shadows.base,
      overflow: 'hidden',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      border: `1px solid ${theme.colors.primary[100]}`,
    },
    hover: {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows.xl,
      borderColor: theme.colors.primary[200],
    }
  }
};

// Animation Variants
const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -60 }
};

const staggerContainer = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const scaleIn = {
  initial: { opacity: 0, scale: 0.8 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.8 }
};

// Data Types
interface Article {
  id: string;
  title: string;
  excerpt: string;
  readingTime: number;
  tags: string[];
  category: 'Technical Writing' | 'Content Strategy' | 'Copywriting' | 'Blog Posts' | 'Documentation';
  image: string;
  publishedAt: string;
  client: string;
  featured: boolean;
}

interface Skill {
  name: string;
  level: number;
  category: 'Writing' | 'Technical' | 'Marketing' | 'Tools';
}

interface Testimonial {
  id: string;
  name: string;
  role: string;
  company: string;
  content: string;
  avatar: string;
  rating: number;
}

// Sample Data
const portfolioData: Article[] = [
  {
    id: '1',
    title: 'Building Scalable API Documentation',
    excerpt: 'A comprehensive guide to creating developer-friendly API documentation that scales with your product and reduces support tickets by 60%.',
    readingTime: 12,
    tags: ['API', 'Documentation', 'Developer Experience'],
    category: 'Technical Writing',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=500&fit=crop&q=80',
    publishedAt: '2024-02-15',
    client: 'Stripe',
    featured: true
  },
  {
    id: '2',
    title: 'Content Strategy for SaaS Growth',
    excerpt: 'How strategic content marketing helped a B2B SaaS company achieve 300% growth in organic traffic and 150% increase in qualified leads.',
    readingTime: 8,
    tags: ['Content Strategy', 'SaaS', 'Growth Marketing'],
    category: 'Content Strategy',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=500&fit=crop&q=80',
    publishedAt: '2024-02-10',
    client: 'Notion',
    featured: true
  },
  {
    id: '3',
    title: 'Converting Landing Page Copy',
    excerpt: 'Crafted high-converting landing page copy that increased conversion rates by 85% for a fintech startup\'s product launch.',
    readingTime: 6,
    tags: ['Copywriting', 'Conversion', 'Fintech'],
    category: 'Copywriting',
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=500&fit=crop&q=80',
    publishedAt: '2024-02-05',
    client: 'Revolut',
    featured: false
  },
  {
    id: '4',
    title: 'React Performance Deep Dive',
    excerpt: 'Technical article exploring advanced React optimization techniques, including code splitting, memoization, and bundle analysis.',
    readingTime: 15,
    tags: ['React', 'Performance', 'JavaScript'],
    category: 'Technical Writing',
    image: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=800&h=500&fit=crop&q=80',
    publishedAt: '2024-01-28',
    client: 'LogRocket',
    featured: false
  },
  {
    id: '5',
    title: 'Email Marketing That Actually Works',
    excerpt: 'A data-driven approach to email marketing that helped e-commerce brands achieve 40% higher open rates and 25% more conversions.',
    readingTime: 10,
    tags: ['Email Marketing', 'E-commerce', 'Analytics'],
    category: 'Content Strategy',
    image: 'https://images.unsplash.com/photo-1596526131083-e8c633c948d2?w=800&h=500&fit=crop&q=80',
    publishedAt: '2024-01-20',
    client: 'Shopify',
    featured: false
  },
  {
    id: '6',
    title: 'Kubernetes Documentation Overhaul',
    excerpt: 'Redesigned and rewrote complex Kubernetes documentation, making it accessible to developers at all skill levels.',
    readingTime: 18,
    tags: ['Kubernetes', 'DevOps', 'Documentation'],
    category: 'Documentation',
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=500&fit=crop&q=80',
    publishedAt: '2024-01-15',
    client: 'CNCF',
    featured: true
  }
];

const clientLogos = [
  { name: 'Tech Corp', icon: Icons.FaLaptopCode },
  { name: 'Data Analytics', icon: Icons.FaChartBar },
  { name: 'Cloud Services', icon: Icons.FaCloud },
  { name: 'E-commerce Plus', icon: Icons.FaShoppingCart },
  { name: 'Dev Tools', icon: Icons.FaCode },
  { name: 'Code Hub', icon: Icons.FaGithub },
  { name: 'Build Fast', icon: Icons.FaRocket },
  { name: 'Design Pro', icon: Icons.FaPaintBrush },
];

const services = [
  {
    title: 'Content Strategy',
    description: 'Comprehensive content planning and strategy development to align with your business goals and target audience needs.',
    features: ['Content Audits', 'Editorial Calendars', 'SEO Strategy', 'Performance Analytics']
  },
  {
    title: 'Copywriting',
    description: 'Persuasive copy that converts visitors into customers, from landing pages to email campaigns.',
    features: ['Landing Pages', 'Email Campaigns', 'Ad Copy', 'Sales Funnels']
  },
  {
    title: 'Technical Writing',
    description: 'Clear, comprehensive documentation that makes complex topics accessible to your users and developers.',
    features: ['API Documentation', 'User Guides', 'Technical Tutorials', 'Developer Resources']
  }
];

const whyWorkWithCopywriter = [
  {
    title: 'Content Attracts Users',
    description: 'Quality content draws potential customers to your brand and establishes authority in your industry.',
    icon: Icons.FaBullseye
  },
  {
    title: 'Words Save Time',
    description: 'Clear communication reduces support requests and helps users find what they need quickly.',
    icon: Icons.FaClock
  },
  {
    title: 'Copy Driving Growth',
    description: 'Strategic copywriting directly impacts conversion rates and business growth metrics.',
    icon: Icons.FaChartLine
  },
  {
    title: 'Save Time & Cost',
    description: 'Professional writing saves you time and reduces the cost of customer acquisition.',
    icon: Icons.FaDollarSign
  }
];

const resumeData = {
  basic: {
    name: 'Alexandra Johnson',
    title: 'Technical Writer & Content Strategist',
    email: 'alexandra@example.com',
    phone: '+1 (555) 123-4567',
    location: 'San Francisco, CA',
    website: 'alexandra-writer.com'
  },
  experience: [
    {
      title: 'Senior Technical Writer',
      company: 'Stripe',
      period: '2022 - Present',
      description: 'Lead documentation strategy for developer tools and APIs',
      achievements: ['Reduced support tickets by 40%', 'Improved developer onboarding experience']
    },
    {
      title: 'Content Strategist',
      company: 'Notion',
      period: '2020 - 2022',
      description: 'Developed content marketing strategy resulting in 300% traffic growth',
      achievements: ['Increased organic traffic by 300%', 'Generated 150% more qualified leads']
    },
    {
      title: 'Freelance Copywriter',
      company: 'Self-Employed',
      period: '2018 - 2020',
      description: 'Provided copywriting services for various startups and enterprises',
      achievements: ['Worked with 50+ clients', 'Average 75% increase in conversion rates']
    }
  ],
  education: [
    {
      degree: 'M.A. in Technical Communication',
      school: 'Stanford University',
      year: '2018'
    },
    {
      degree: 'B.A. in English Literature',
      school: 'UC Berkeley',
      year: '2016'
    }
  ],
  certifications: [
    'Google Analytics Certified',
    'HubSpot Content Marketing Certification',
    'Technical Writing Certificate - UC Extension'
  ]
};

const skills: Skill[] = [
  { name: 'Technical Writing', level: 95, category: 'Writing' },
  { name: 'Content Strategy', level: 90, category: 'Marketing' },
  { name: 'API Documentation', level: 88, category: 'Technical' },
  { name: 'Copywriting', level: 85, category: 'Writing' },
  { name: 'SEO Writing', level: 82, category: 'Marketing' },
  { name: 'Developer Tools', level: 80, category: 'Technical' },
  { name: 'Figma', level: 75, category: 'Tools' },
  { name: 'Analytics', level: 78, category: 'Tools' }
];

const testimonials: Testimonial[] = [
  {
    id: '1',
    name: 'Sarah Chen',
    role: 'Product Manager',
    company: 'Stripe',
    content: 'Alexandra transformed our API documentation from confusing to crystal clear. Developer adoption increased by 40% after her rewrite.',
    avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b5bc?w=64&h=64&fit=crop&crop=face&q=80',
    rating: 5
  },
  {
    id: '2',
    name: 'Marcus Rodriguez',
    role: 'Head of Growth',
    company: 'Notion',
    content: 'The content strategy Alex developed drove our highest-performing quarter. Her data-driven approach to storytelling is exceptional.',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=64&h=64&fit=crop&crop=face&q=80',
    rating: 5
  },
  {
    id: '3',
    name: 'Emma Thompson',
    role: 'Marketing Director',
    company: 'Revolut',
    content: 'Alexandra\'s conversion-focused copy increased our landing page performance by 85%. She understands both psychology and technology.',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=64&h=64&fit=crop&crop=face&q=80',
    rating: 5
  }
];

// Custom Hooks
const useScrollAnimation = () => {
  const controls = useAnimation();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-20%" });

  useEffect(() => {
    if (inView) {
      controls.start('animate');
    }
  }, [controls, inView]);

  return { ref, controls };
};

const useActiveSection = () => {
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'about', 'services', 'portfolio', 'resume', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return activeSection;
};

// Components
const Navigation: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const activeSection = useActiveSection();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleWorkTogether = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navItems = [
    { name: 'ABOUT', href: '#about' },
    { name: 'SERVICES', href: '#services' },
    { name: 'WORK', href: '#portfolio' },
    { name: 'RESUME', href: '#resume' },
    { name: 'CONTACT', href: '#contact' }
  ];

  return (
    <>
    <motion.nav
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] }}
        className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200"
    >
        <div className="max-w-6xl mx-auto flex items-center justify-between h-[70px] px-6">
          {/* Logo */}
        <motion.div
            whileHover={{ scale: 1.02 }}
            className="cursor-pointer"
          >
            <div className="text-2xl font-normal text-gray-900 font-display tracking-wider">
              Freelance Writer
            </div>
        </motion.div>

          {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-10">
            {navItems.map((item, index) => (
            <motion.a
              key={item.name}
              href={item.href}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index + 0.2, duration: 0.5 }}
                whileHover={{ y: -1 }}
                className={`text-sm font-normal transition-all duration-200 relative font-display tracking-wider py-2 ${
                  activeSection === item.href.slice(1) 
                    ? 'text-gray-900' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              {item.name}
              {activeSection === item.href.slice(1) && (
                <motion.div
                    layoutId="activeNavIndicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-400"
                />
              )}
            </motion.a>
          ))}
        </div>

          {/* CTA Button */}
        <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleWorkTogether}
            className="hidden md:block bg-yellow-400 text-gray-900 px-6 py-3 text-sm font-normal font-display tracking-wider transition-all duration-200 hover:bg-yellow-500"
        >
            LET'S WORK TOGETHER
          </motion.button>

          {/* Mobile Menu Button */}
          <motion.button
            className="md:hidden flex items-center justify-center w-10 h-10 bg-transparent border-none cursor-pointer"
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <div className="flex flex-col gap-[3px]">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  animate={{
                    rotate: isMobileMenuOpen && i === 0 ? 45 : isMobileMenuOpen && i === 2 ? -45 : 0,
                    y: isMobileMenuOpen && i === 0 ? 6 : isMobileMenuOpen && i === 2 ? -6 : 0,
                    opacity: isMobileMenuOpen && i === 1 ? 0 : 1,
                  }}
                  className="w-5 h-0.5 bg-gray-700 rounded-sm"
                />
              ))}
            </div>
        </motion.button>
      </div>
    </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-[70px] left-0 right-0 bg-white border-b border-gray-100 z-40 p-6"
          >
            {navItems.map((item, index) => (
              <motion.a
                key={item.name}
                href={item.href}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block py-3 text-base font-normal text-gray-700 font-display tracking-wider ${
                  index < navItems.length - 1 ? 'border-b border-gray-100' : ''
                }`}
              >
                {item.name}
              </motion.a>
            ))}
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              onClick={handleWorkTogether}
              className="bg-yellow-400 text-gray-900 px-6 py-3 text-sm font-normal font-display tracking-wider mt-4 w-full hover:bg-yellow-500 transition-colors"
            >
              LET'S WORK TOGETHER
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const Services: React.FC = () => {
  const { ref, controls } = useScrollAnimation();

  // Enhanced services data with icons and numbers
  const enhancedServices = [
    {
      ...services[0],
      icon: HiOutlineDocumentText,
      number: '01',
      accent: 'blue'
    },
    {
      ...services[1],
      icon: HiOutlinePencilAlt,
      number: '02',
      accent: 'emerald'
    },
    {
      ...services[2],
      icon: HiOutlineCode,
      number: '03',
      accent: 'yellow'
    }
  ];

  const getAccentColors = (accent: string) => {
    switch (accent) {
      case 'blue':
        return {
          icon: 'text-blue-600',
          bg: 'bg-blue-50',
          border: 'border-blue-100',
          hover: 'hover:border-blue-200'
        };
      case 'emerald':
        return {
          icon: 'text-emerald-600',
          bg: 'bg-emerald-50',
          border: 'border-emerald-100',
          hover: 'hover:border-emerald-200'
        };
      case 'yellow':
        return {
          icon: 'text-yellow-600',
          bg: 'bg-yellow-50',
          border: 'border-yellow-100',
          hover: 'hover:border-yellow-200'
        };
      default:
        return {
          icon: 'text-gray-600',
          bg: 'bg-gray-50',
          border: 'border-gray-100',
          hover: 'hover:border-gray-200'
        };
    }
  };

  return (
    <section id="services" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <motion.div
          ref={ref}
          initial="initial"
          animate={controls}
          variants={staggerContainer}
          className="text-center mb-20"
        >
          {/* Section Label */}
          <motion.div
            variants={fadeInUp}
            className="inline-flex items-center gap-2 bg-white px-4 py-2 text-sm text-gray-600 font-display tracking-wider uppercase mb-6 shadow-sm"
          >
            <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
            What I Offer
          </motion.div>

          <motion.h2
            variants={fadeInUp}
            className="text-4xl md:text-5xl font-normal text-gray-900 mb-8 font-display tracking-wide leading-tight"
          >
            Services
          </motion.h2>
          
          <motion.p
            variants={fadeInUp}
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-sans"
          >
            Comprehensive writing services designed to help your business communicate 
            effectively and drive meaningful results through strategic content.
          </motion.p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {enhancedServices.map((service, index) => {
            const IconComponent = service.icon as React.ComponentType<{ className?: string }>;
            const colors = getAccentColors(service.accent);
            
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.15, duration: 0.6 }}
                whileHover={{ y: -8 }}
                className={`bg-white p-8 lg:p-10 border ${colors.border} ${colors.hover} shadow-sm hover:shadow-xl transition-all duration-300 group relative overflow-hidden`}
              >
                {/* Service Number */}
                <div className="absolute top-6 right-6 text-6xl font-display text-gray-100 font-bold leading-none select-none">
                  {service.number}
                </div>

                {/* Icon Container */}
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  className={`${colors.bg} w-16 h-16 rounded-full flex items-center justify-center mb-8 relative z-10`}
                >
                  <IconComponent className={`w-8 h-8 ${colors.icon}`} />
                </motion.div>

                {/* Content */}
                <div className="relative z-10">
                  <h3 className="text-2xl font-normal text-gray-900 mb-6 font-display tracking-wide">
                    {service.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed mb-8 font-sans text-base">
                    {service.description}
                  </p>

                  {/* Features List */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-gray-800 uppercase tracking-wider font-display mb-4">
                      What's Included
                    </h4>
                    <ul className="space-y-3">
                      {service.features.map((feature, featureIndex) => (
                        <motion.li
                          key={feature}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: (index * 0.15) + (featureIndex * 0.05) + 0.3 }}
                          className="flex items-center gap-3 text-sm text-gray-700 font-sans"
                        >
                          <div className={`w-2 h-2 ${colors.icon.replace('text-', 'bg-')} rounded-full flex-shrink-0`} />
                          <span className="group-hover:text-gray-900 transition-colors duration-200">
                            {feature}
                          </span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Learn More Link */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.15 + 0.5 }}
                    className="mt-8 pt-6 border-t border-gray-100"
                  >
                    <button className="text-sm font-display tracking-wider text-gray-600 hover:text-gray-900 transition-colors duration-200 uppercase flex items-center gap-2 group/button">
                      Learn More
                      <svg 
                        className="w-3 h-3 group-hover/button:translate-x-1 transition-transform duration-200" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </motion.div>
                </div>

                {/* Hover Effect Background */}
                <div className={`absolute inset-0 ${colors.bg} opacity-0 group-hover:opacity-50 transition-opacity duration-300`} />
              </motion.div>
            );
          })}
        </div>

     
      </div>
    </section>
  );
};

const About: React.FC = () => {
  const { ref, controls } = useScrollAnimation();
  const [downloadState, setDownloadState] = useState<'idle' | 'downloading' | 'success'>('idle');

  const handleDownloadPDF = async () => {
    setDownloadState('downloading');
    
    // Create a mock PDF blob (in a real app, you'd fetch from your server)
    const createMockPDF = () => {
      const content = `
Alexandra Wilson - Freelance Writer & Content Strategist
Email: alex@example.com | Phone: (555) 123-4567 | Location: San Francisco, CA

PROFESSIONAL SUMMARY
Experienced freelance writer and content strategist with 5+ years of expertise in technical writing, content strategy, and copywriting. Proven track record of helping startups and enterprises improve their communication and drive growth through strategic content.

CORE EXPERTISE
• Technical Writing & Documentation
• Content Strategy & Planning  
• Creative Copywriting
• SEO Optimization
• Marketing Copy
• Brand Storytelling

EXPERIENCE
Freelance Writer & Content Strategist (2019 - Present)
• Completed 100+ projects for 50+ clients
• Specializing in technical documentation, marketing copy, and content strategy
• 95% client satisfaction rate with strong repeat business

Senior Content Writer, Tech Corp (2017 - 2019)
• Led content strategy for major product launches
• Increased user engagement by 40% through improved documentation
• Collaborated with cross-functional teams on content planning

EDUCATION
Bachelor of Arts in English Literature
University of California, Berkeley (2015 - 2017)

CERTIFICATIONS
• Google Analytics Certified
• HubSpot Content Marketing Certified
• Technical Writing Certificate

SKILLS
Writing: Technical Writing, Copywriting, Content Strategy, SEO Writing
Technical: Google Analytics, HubSpot, WordPress, Figma
Marketing: Email Marketing, Social Media, Brand Strategy
Tools: Notion, Slack, Trello, Adobe Creative Suite
      `;
      
      return new Blob([content], { type: 'text/plain' });
    };

    try {
      // Simulate download delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const blob = createMockPDF();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Alexandra_Wilson_Resume.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setDownloadState('success');
      setTimeout(() => setDownloadState('idle'), 3000);
    } catch (error) {
      setDownloadState('idle');
    }
  };

  const getDownloadButtonContent = () => {
    switch (downloadState) {
      case 'downloading':
        return (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            DOWNLOADING...
          </>
        );
      case 'success':
        return (
          <>
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            DOWNLOADED!
          </>
        );
      default:
        return (
          <>
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            DOWNLOAD RESUME
          </>
        );
    }
  };

  return (
    <section id="about" className="py-24 md:py-32 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-40">
        <div className="absolute top-20 right-10 w-32 h-32 bg-yellow-100 rounded-full mix-blend-multiply"></div>
        <div className="absolute bottom-20 left-10 w-40 h-40 bg-blue-100 rounded-full mix-blend-multiply"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          
          {/* Left side - Enhanced Image Section */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            {/* Section Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-yellow-400 px-4 py-2 text-sm text-gray-900 font-display tracking-wider uppercase mb-8"
            >
              <div className="w-2 h-2 bg-gray-900 rounded-full"></div>
              About Me
            </motion.div>

            <div className="relative max-w-md mx-auto lg:mx-0">
              {/* Main Image */}
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=500&h=600&fit=crop&crop=face&q=80"
                  alt="Alexandra - Freelance Writer"
                  className="w-full h-[500px] object-cover shadow-2xl"
                />
                
                {/* Floating Stats Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="absolute -bottom-6 -right-6 bg-white p-6 shadow-xl border border-gray-100"
                >
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 font-display mb-1">5+</div>
                    <div className="text-sm text-gray-600 font-sans">Years Experience</div>
                  </div>
                </motion.div>

                {/* Floating Expertise Badge */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 }}
                  className="absolute -left-6 top-1/3 bg-emerald-500 text-white p-4 shadow-xl transform -rotate-3"
                >
                  <div className="text-center">
                    <div className="text-sm font-semibold font-display">Freelance</div>
                    <div className="text-xs">Specialist</div>
                  </div>
                </motion.div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -top-4 -left-4 w-16 h-16 border-4 border-yellow-400 opacity-60"></div>
              <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-yellow-200 rounded-full opacity-30"></div>
            </div>
          </motion.div>

          {/* Right side - Enhanced Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            {/* Greeting */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <h2 className="text-4xl md:text-5xl font-normal text-gray-900 mb-6 font-display tracking-wide">
                Hello! I'm{' '}
                <span className="relative">
                  Alexandra
                  <div className="absolute -bottom-2 left-0 right-0 h-3 bg-yellow-300 opacity-60 -z-10"></div>
                </span>
              </h2>
              
              <div className="text-xl text-emerald-600 font-display tracking-wide mb-8">
                Freelance Writer & Content Strategist
              </div>
            </motion.div>

            {/* Main Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="space-y-6"
            >
              <p className="text-lg text-gray-700 leading-relaxed font-sans">
                A passionate <strong>freelance writer</strong> based in San Francisco with over 5 years of experience 
                helping businesses tell their stories effectively. I specialize in creating content 
                that not only engages audiences but also drives measurable business results.
              </p>

              <p className="text-base text-gray-600 leading-relaxed font-sans">
                As an independent freelance writer, I work with startups to Fortune 500 companies, 
                crafting everything from technical documentation to compelling marketing copy. 
                My freelance approach means you get dedicated attention and customized solutions 
                for your unique content challenges.
              </p>
            </motion.div>

            {/* Expertise Highlights */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="space-y-4"
            >
              <h3 className="text-lg font-semibold text-gray-900 font-display tracking-wide">
                Freelance Writing Expertise
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { 
                    icon: (
                      <svg className="w-5 h-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    ), 
                    text: 'Technical Writing' 
                  },
                  { 
                    icon: (
                      <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    ), 
                    text: 'Content Strategy' 
                  },
                  { 
                    icon: (
                      <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    ), 
                    text: 'Creative Copywriting' 
                  },
                  { 
                    icon: (
                      <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    ), 
                    text: 'Growth Marketing' 
                  }
                ].map((item, index) => (
                  <motion.div
                    key={item.text}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.8 + (index * 0.1) }}
                    className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 transition-colors duration-200 group"
                  >
                    <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow duration-200">
                      {item.icon}
                    </div>
                    <span className="text-sm font-medium text-gray-800 font-sans">{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Personal Touch */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0 }}
              className="p-6 bg-yellow-50 border-l-4 border-yellow-400"
            >
              <p className="text-sm text-gray-700 italic font-sans leading-relaxed">
                "When I'm not crafting compelling content for my freelance clients, you'll find me 
                exploring the latest content marketing trends, hiking the beautiful trails around 
                San Francisco, or perfecting my coffee brewing techniques. I believe great writing 
                comes from a rich life of experiences."
              </p>
            </motion.div>

            {/* Call to Action */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
              className="flex flex-wrap gap-4"
            >
              <motion.button
                onClick={handleDownloadPDF}
                disabled={downloadState === 'downloading'}
                whileHover={downloadState === 'idle' ? { scale: 1.02 } : {}}
                whileTap={downloadState === 'idle' ? { scale: 0.98 } : {}}
                className={`px-8 py-4 text-base font-normal font-display tracking-wider transition-all duration-200 shadow-lg flex items-center justify-center min-w-[200px] ${
                  downloadState === 'downloading' 
                    ? 'bg-gray-600 text-white cursor-not-allowed' 
                    : downloadState === 'success'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                {getDownloadButtonContent()}
              </motion.button>
              
              <motion.button
                onClick={() => document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' })}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="border-2 border-gray-900 text-gray-900 px-8 py-4 text-base font-normal font-display tracking-wider transition-all duration-200 hover:bg-gray-900 hover:text-white flex items-center justify-center min-w-[200px]"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0l-4-4m4 4l-4 4" />
                </svg>
                VIEW PORTFOLIO
              </motion.button>
            </motion.div>
          </motion.div>
        </div>

        
      </div>
    </section>
  );
};

const ClientLogos: React.FC = () => {
  const { ref, controls } = useScrollAnimation();

  // Enhanced client data with additional information
  const enhancedClientLogos = [
    { ...clientLogos[0], category: 'Technology', size: 'Enterprise' },
    { ...clientLogos[1], category: 'Analytics', size: 'Scale-up' },
    { ...clientLogos[2], category: 'Infrastructure', size: 'Enterprise' },
    { ...clientLogos[3], category: 'E-commerce', size: 'Growth' },
    { ...clientLogos[4], category: 'Developer Tools', size: 'Scale-up' },
    { ...clientLogos[5], category: 'Platform', size: 'Enterprise' },
    { ...clientLogos[6], category: 'DevOps', size: 'Growth' },
    { ...clientLogos[7], category: 'Design', size: 'Scale-up' },
  ];

  return (
    <section className="py-20 bg-gray-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-1/4 w-72 h-72 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <motion.div
          ref={ref}
          initial="initial"
          animate={controls}
          variants={staggerContainer}
          className="text-center mb-16 md:mb-20"
        >
          {/* Section Badge */}
          <motion.div
            variants={fadeInUp}
            className="inline-flex items-center gap-2 bg-white px-4 py-2 text-sm text-gray-600 font-display tracking-wider uppercase mb-6 shadow-sm border border-gray-100"
          >
            <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
            Client Portfolio
          </motion.div>

          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-normal text-gray-900 mb-6 font-display tracking-wide"
          >
            Trusted by Leading Companies
          </motion.h2>
          
          <motion.p
            variants={fadeInUp}
            className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed font-sans mb-12"
          >
            From emerging startups to Fortune 500 companies, I've helped diverse organizations 
            transform their content strategy and achieve measurable results.
          </motion.p>

          {/* Stats Row */}
          <motion.div
            variants={fadeInUp}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-2xl mx-auto mb-16"
          >
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 font-display mb-2">50+</div>
              <div className="text-sm text-gray-600 font-sans">Projects Completed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 font-display mb-2">8</div>
              <div className="text-sm text-gray-600 font-sans">Industries Served</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 font-display mb-2">95%</div>
              <div className="text-sm text-gray-600 font-sans">Client Satisfaction</div>
            </div>
          </motion.div>
        </motion.div>

        {/* Desktop - Enhanced Grid Layout */}
        <div className="hidden md:block">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {enhancedClientLogos.map((client, index) => {
              const IconComponent = client.icon as React.ComponentType<{ className?: string }>;
              return (
                <motion.div
                  key={client.name}
                  initial={{ opacity: 0, y: 30, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ delay: index * 0.08, duration: 0.5 }}
                  whileHover={{ 
                    y: -8, 
                    scale: 1.05,
                    transition: { duration: 0.2 }
                  }}
                  className="bg-white p-8 border border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-lg transition-all duration-300 group relative overflow-hidden"
                >
                  {/* Category Badge */}
                  <div className="absolute top-3 right-3 bg-gray-100 text-gray-600 text-xs px-2 py-1 font-display tracking-wider">
                    {client.size}
                  </div>

                  {/* Icon and Content */}
                  <div className="flex flex-col items-center text-center">
                    <motion.div
                      whileHover={{ rotate: 5, scale: 1.1 }}
                      className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4 group-hover:bg-yellow-50 transition-colors duration-300"
                    >
                      <IconComponent className="text-3xl text-gray-600 group-hover:text-yellow-600 transition-colors duration-300" />
                    </motion.div>
                    
                    <h3 className="text-lg font-normal text-gray-900 mb-2 font-display tracking-wide group-hover:text-gray-800">
                      {client.name}
                    </h3>
                    
                    <p className="text-sm text-gray-500 font-sans">
                      {client.category}
                    </p>
                  </div>

                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Mobile - Horizontal Scroll */}
        <div className="md:hidden">
          <div className="relative py-4">
            {/* Gradient Overlays */}
            <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-gray-50 to-transparent z-10 pointer-events-none"></div>
            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-gray-50 to-transparent z-10 pointer-events-none"></div>
            
            {/* Horizontal Scroll Container */}
            <div className="overflow-x-auto scrollbar-hide">
              <div className="flex gap-6 px-4" style={{ width: 'max-content' }}>
                {enhancedClientLogos.map((client, index) => {
                  const IconComponent = client.icon as React.ComponentType<{ className?: string }>;
                  return (
                    <motion.div
                      key={`scroll-${client.name}-${index}`}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-white p-6 border border-gray-200 shadow-sm min-w-[160px] flex flex-col items-center text-center hover:shadow-lg hover:border-gray-300 transition-all duration-300 flex-shrink-0"
                    >
                      <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-3 hover:bg-yellow-50 transition-colors duration-300">
                        <IconComponent className="text-2xl text-gray-600" />
                      </div>
                      <h3 className="text-base font-normal text-gray-900 mb-1 font-display tracking-wide">
                        {client.name}
                      </h3>
                      <p className="text-xs text-gray-500 font-sans">
                        {client.category}
                      </p>
                    </motion.div>
                  );
                })}
              </div>
            </div>
            
           
          </div>
        </div>

        {/* Bottom Testimonial Quote */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="text-center mt-20 max-w-4xl mx-auto"
        >
          <div className="bg-white p-8 md:p-12 border border-gray-200 shadow-lg relative">
            {/* Quote Mark */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-yellow-400 flex items-center justify-center text-gray-900 text-xl font-bold">
              "
            </div>
            
            <blockquote className="text-xl md:text-2xl text-gray-700 font-normal italic mb-6 font-sans leading-relaxed">
              "Working with Alexandra has transformed how we communicate with our users. 
              Her strategic approach to content has directly impacted our growth metrics."
            </blockquote>
            
            <div className="flex items-center justify-center gap-4">
              <img
                src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=48&h=48&fit=crop&crop=face&q=80"
                alt="Client testimonial"
                className="w-12 h-12 rounded-full object-cover"
              />
              <div className="text-left">
                <div className="font-semibold text-gray-900 font-display">Marcus Chen</div>
                <div className="text-sm text-gray-600 font-sans">VP of Growth, Tech Corp</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Portfolio: React.FC = () => {
  const { ref, controls } = useScrollAnimation();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedTag, setSelectedTag] = useState<string>('all');
  
  // Extract all available categories
  const categories = ['all', ...Array.from(new Set(portfolioData.map(item => item.category)))];
  
  // Extract all unique tags from portfolio data
  const allTags = portfolioData.reduce((tags, item) => {
    item.tags.forEach(tag => tags.add(tag));
    return tags;
  }, new Set<string>());
  const tags = ['all', ...Array.from(allTags)];
  
  // Filter portfolio items by both category and tag
  const filteredPortfolio = portfolioData.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesTag = selectedTag === 'all' || item.tags.includes(selectedTag);
    return matchesCategory && matchesTag;
  });

  return (
    <section id="portfolio" className="py-24 md:py-32 bg-gray-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 right-20 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl animate-pulse" style={{ animationDelay: '3s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <motion.div
          ref={ref}
          initial="initial"
          animate={controls}
          variants={staggerContainer}
          className="text-center mb-20"
        >
          {/* Section Badge */}
          <motion.div
            variants={fadeInUp}
            className="inline-flex items-center gap-2 bg-white px-4 py-2 text-sm text-gray-600 font-display tracking-wider uppercase mb-8 shadow-sm border border-gray-100"
          >
            <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
            Featured Work
          </motion.div>

          <motion.h2
            variants={fadeInUp}
            className="text-4xl md:text-5xl font-normal text-gray-900 mb-8 font-display tracking-wide leading-tight"
          >
            From The Blog
          </motion.h2>
          
          <motion.p
            variants={fadeInUp}
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-sans mb-12"
          >
            Insights, strategies, and lessons learned from years of crafting 
            compelling content for diverse industries and freelance projects.
          </motion.p>

          {/* Simple Stats */}
          <motion.div
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-8 text-sm text-gray-500 font-sans">
              <span>50+ Articles</span>
              <span>•</span>
              <span>5 Industries</span>
              <span>•</span>
              <span>95% Engagement</span>
                </div>
          </motion.div>
        </motion.div>

        {/* Simplified Filter Interface */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-16"
        >
          <div className="max-w-4xl mx-auto">
            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
            {categories.map((category, index) => (
              <motion.button
                key={category}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 + (index * 0.05) }}
                onClick={() => setSelectedCategory(category)}
                  whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                  className={`px-8 py-3 text-sm font-display tracking-wider transition-all duration-300 ${
                  selectedCategory === category 
                      ? 'bg-yellow-400 text-gray-900 shadow-md' 
                      : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                  {category === 'all' ? 'All Work' : category}
              </motion.button>
            ))}
          </div>

            {/* Popular Tags - Show only if needed */}
            {selectedCategory === 'all' && (
        <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.3 }}
                className="text-center"
              >
                <p className="text-sm text-gray-500 mb-4 font-sans">Popular topics:</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {['API', 'SaaS', 'Content Strategy', 'Technical', 'Growth'].map((tag) => (
              <motion.button
                key={tag}
                onClick={() => setSelectedTag(tag)}
                      whileHover={{ scale: 1.05 }}
                      className={`px-3 py-1 text-xs font-display tracking-wider rounded-full transition-all duration-300 ${
                  selectedTag === tag 
                          ? 'bg-gray-900 text-white' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                      {tag}
              </motion.button>
            ))}
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Simple Results Summary */}
        {filteredPortfolio.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          className="mb-12 text-center"
        >
            <div className="text-gray-500 font-sans">
              No articles found for the selected filters
          </div>
        </motion.div>
        )}

        {/* Enhanced Portfolio Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {filteredPortfolio.length > 0 ? (
            filteredPortfolio.map((item, index) => (
              <motion.article
                key={item.id}
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ 
                  y: -8,
                  transition: { duration: 0.3 }
                }}
                className="bg-white border border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-xl transition-all duration-300 group relative overflow-hidden"
              >
                {/* Image Section */}
                <div className="relative overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  
                  {/* Featured Badge */}
                  {item.featured && (
                    <div className="absolute top-4 left-4 bg-yellow-400 text-gray-900 px-3 py-2 text-xs font-bold font-display tracking-wider z-10 shadow-md">
                      FEATURED
                    </div>
                  )}
                  
                  {/* Reading Time Badge */}
                  <div className="absolute top-4 right-4 bg-gray-900/90 backdrop-blur-sm text-white px-3 py-2 text-xs font-display tracking-wider flex items-center gap-2 shadow-lg">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {item.readingTime} min
                  </div>

                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                
                {/* Content Section */}
                <div className="p-8 flex flex-col flex-grow">
                  {/* Category and Date */}
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-xs text-gray-500 font-display tracking-wider uppercase">
                      {item.category}
                    </span>
                    <span className="text-xs text-gray-400 font-sans">
                      {new Date(item.publishedAt).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-normal text-gray-900 mb-4 font-display tracking-wide group-hover:text-yellow-600 transition-colors duration-300 leading-tight">
                    {item.title}
                  </h3>
                  
                  {/* Excerpt */}
                  <p className="text-gray-600 text-sm leading-relaxed mb-6 font-sans line-clamp-3 flex-grow">
                    {item.excerpt}
                  </p>
                  
                  {/* Footer with Client and Read More */}
                  <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                        <span className="text-xs font-semibold text-gray-600 font-display">
                          {item.client.charAt(0)}
                      </span>
                  </div>
                      <span className="text-xs text-gray-600 font-sans">{item.client}</span>
                  </div>

                  {/* Read More Button */}
                  <motion.button
                    whileHover={{ x: 5 }}
                      className="text-xs font-display tracking-wider text-gray-500 hover:text-gray-900 transition-colors duration-200 flex items-center gap-1 group/button"
                  >
                      Read
                      <svg className="w-3 h-3 group-hover/button:translate-x-1 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </motion.button>
                  </div>
                </div>

                {/* Hover Effect Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              </motion.article>
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              className="col-span-1 md:col-span-2 lg:col-span-3 py-20 text-center"
            >
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-6 flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.562M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 font-display">No Articles Found</h3>
                <p className="text-gray-500 font-sans mb-6">No articles match your current filters. Try adjusting your selection.</p>
                <motion.button 
                  onClick={() => {
                    setSelectedCategory('all');
                    setSelectedTag('all');
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-6 py-3 font-display tracking-wider transition-all duration-200 shadow-md"
                >
                  RESET FILTERS
                </motion.button>
              </div>
            </motion.div>
          )}
        </div>

      
      </div>
    </section>
  );
};

const WhyWorkWithCopywriter: React.FC = () => {
  const { ref, controls } = useScrollAnimation();

  // Enhanced benefits data focused on freelance writing
  const enhancedBenefits = [
    {
      ...whyWorkWithCopywriter[0],
      number: '01',
      detail: 'Strategic content that converts visitors into customers',
      metric: '40% avg increase'
    },
    {
      ...whyWorkWithCopywriter[1],
      number: '02', 
      detail: 'Clear communication reduces support burden',
      metric: '60% less tickets'
    },
    {
      ...whyWorkWithCopywriter[2],
      number: '03',
      detail: 'Proven strategies that scale your business',
      metric: '3x faster growth'
    },
    {
      ...whyWorkWithCopywriter[3],
      number: '04',
      detail: 'Direct freelance collaboration without overhead',
      metric: '50% cost savings'
    }
  ];

  return (
    <section className="py-24 md:py-32 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-yellow-200 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl animate-pulse" style={{ animationDelay: '3s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <motion.div
          ref={ref}
          initial="initial"
          animate={controls}
          variants={staggerContainer}
          className="text-center mb-20"
        >
          {/* Section Badge */}
          <motion.div
            variants={fadeInUp}
            className="inline-flex items-center gap-2 bg-white px-4 py-2 text-sm text-gray-600 font-display tracking-wider uppercase mb-8 shadow-sm border border-gray-100"
          >
            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
            The Freelance Advantage
          </motion.div>

          <motion.h2
            variants={fadeInUp}
            className="text-4xl md:text-5xl font-normal text-gray-900 mb-8 font-display tracking-wide leading-tight"
          >
            Why Work With a{' '}
            <span className="relative">
              Freelance Writer?
              <div className="absolute -bottom-2 left-0 right-0 h-3 bg-yellow-300 opacity-60 -z-10"></div>
            </span>
          </motion.h2>
          
          <motion.p
            variants={fadeInUp}
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-sans mb-12"
          >
            As an independent freelance writer, I deliver personalized attention, specialized expertise, 
            and measurable results that larger agencies simply can't match.
          </motion.p>

          {/* Quick Stats */}
          <motion.div
            variants={fadeInUp}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto mb-16"
          >
            {[
              { number: '95%', label: 'Client Retention' },
              { number: '48hr', label: 'Response Time' },
              { number: '5+', label: 'Years Experience' },
              { number: '50+', label: 'Projects Delivered' }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + (index * 0.1) }}
                className="text-center"
              >
                <div className="text-2xl md:text-3xl font-bold text-gray-900 font-display mb-1">
                  {stat.number}
                </div>
                <div className="text-sm text-gray-600 font-sans">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Enhanced Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {enhancedBenefits.map((item, index) => {
            const IconComponent = item.icon as React.ComponentType<{ className?: string }>;
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 40, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: index * 0.15, duration: 0.6 }}
                whileHover={{ 
                  y: -12, 
                  transition: { duration: 0.3, ease: "easeOut" }
                }}
                className="bg-white p-8 border border-gray-200 shadow-sm group relative overflow-hidden cursor-pointer hover-card"
                style={{
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                {/* Hover Effect Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-50 via-yellow-25 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 ease-out" />
                
                {/* Border Effect */}
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-yellow-200 transition-all duration-300 ease-out" />

                {/* Number Badge */}
                <div className="absolute top-6 right-6 w-8 h-8 bg-gray-100 text-gray-600 text-sm font-bold font-display flex items-center justify-center group-hover:bg-yellow-400 group-hover:text-gray-900 transition-all duration-300 ease-out z-10">
                  {item.number}
                </div>

                {/* Icon Container */}
                <motion.div
                  whileHover={{ 
                    rotate: 8, 
                    scale: 1.1,
                    transition: { duration: 0.3, ease: "easeOut" }
                  }}
                  className="w-16 h-16 bg-yellow-50 rounded-full flex items-center justify-center mb-6 group-hover:bg-yellow-100 group-hover:shadow-lg transition-all duration-300 ease-out relative z-10"
                >
                  <IconComponent className="text-3xl text-yellow-600 group-hover:text-yellow-700 transition-colors duration-300 ease-out" />
                </motion.div>

                {/* Content */}
                <div className="space-y-4 relative z-10">
                  <h3 className="text-xl font-normal text-gray-900 font-display tracking-wide group-hover:text-gray-800 transition-colors duration-300 ease-out">
                    {item.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed font-sans text-sm group-hover:text-gray-700 transition-colors duration-300 ease-out">
                    {item.description}
                  </p>

                  {/* Enhanced Detail */}
                  <div className="pt-4 border-t border-gray-100 group-hover:border-gray-200 transition-colors duration-300 ease-out">
                    <p className="text-xs text-gray-500 font-sans mb-2 group-hover:text-gray-600 transition-colors duration-300 ease-out">
                      {item.detail}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-emerald-600 font-display group-hover:text-emerald-700 transition-colors duration-300 ease-out">
                        {item.metric}
                      </span>
                      <motion.div 
                        className="w-2 h-2 bg-emerald-400 rounded-full group-hover:bg-emerald-500 transition-colors duration-300 ease-out"
                        whileHover={{ 
                          scale: 1.5,
                          transition: { duration: 0.2 }
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Shadow Enhancement */}
                <div className="absolute inset-0 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-out" style={{ boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)' }} />
              </motion.div>
            );
          })}
        </div>

      
      </div>
    </section>
  );
};

const StatsSection: React.FC = () => {
  const { ref, controls } = useScrollAnimation();

  const stats = [
    { 
      number: '50+', 
      label: 'Projects Completed', 
      icon: HiOutlineChartBar,
      description: 'Successful freelance writing projects delivered',
      color: 'blue'
    },
    { 
      number: '75%', 
      label: 'Average Conversion Increase', 
      icon: Icons.FaChartLine,
      description: 'Average improvement in client conversion rates',
      color: 'emerald'
    },
    { 
      number: '95%', 
      label: 'Client Satisfaction Rate', 
      icon: Icons.FaSmile,
      description: 'Clients who recommend my freelance services',
      color: 'purple'
    },
    { 
      number: '48hr', 
      label: 'Response Time', 
      icon: Icons.FaClock,
      description: 'Fast turnaround on all freelance projects',
      color: 'orange'
    }
  ];

  const getStatColors = (color: string) => {
    switch (color) {
      case 'blue':
        return {
          bg: 'bg-blue-500/20',
          border: 'border-blue-300/30',
          icon: 'text-blue-700',
          accent: 'bg-blue-500'
        };
      case 'emerald':
        return {
          bg: 'bg-emerald-500/20',
          border: 'border-emerald-300/30',
          icon: 'text-emerald-700',
          accent: 'bg-emerald-500'
        };
      case 'purple':
        return {
          bg: 'bg-purple-500/20',
          border: 'border-purple-300/30',
          icon: 'text-purple-700',
          accent: 'bg-purple-500'
        };
      case 'orange':
        return {
          bg: 'bg-orange-500/20',
          border: 'border-orange-300/30',
          icon: 'text-orange-700',
          accent: 'bg-orange-500'
        };
      default:
        return {
          bg: 'bg-gray-500/20',
          border: 'border-gray-300/30',
          icon: 'text-gray-700',
          accent: 'bg-gray-500'
        };
    }
  };

  return (
    <section className="py-24 md:py-32 bg-gradient-to-br from-yellow-400 via-yellow-300 to-yellow-500 relative overflow-hidden">
      {/* Enhanced Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.2)_0%,transparent_50%)]"></div>
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_70%_70%,rgba(0,0,0,0.05)_0%,transparent_50%)]"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-1/4 w-32 h-32 bg-white/10 rounded-full animate-pulse"></div>
        <div className="absolute bottom-20 right-1/3 w-24 h-24 bg-white/15 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 right-1/4 w-16 h-16 bg-gray-900/10 rounded-full animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <motion.div
          ref={ref}
          initial="initial"
          animate={controls}
          variants={staggerContainer}
          className="text-center mb-16 md:mb-20"
        >
          {/* Section Badge */}
          <motion.div
            variants={fadeInUp}
            className="inline-flex items-center gap-2 bg-gray-900 text-white px-4 py-2 text-sm font-display tracking-wider uppercase mb-8 shadow-lg"
          >
            <div className="w-2 h-2 bg-yellow-300 rounded-full"></div>
            Proven Results
          </motion.div>

          <motion.h2
            variants={fadeInUp}
            className="text-4xl md:text-5xl lg:text-6xl font-normal text-gray-900 mb-6 font-display tracking-wide leading-tight"
          >
            Strategic Business
          </motion.h2>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl font-normal text-gray-800 mb-8 font-display tracking-wide leading-tight"
          >
            Requires Strategic Content.
          </motion.h2>
          
          <motion.p
            variants={fadeInUp}
            className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto mb-12 font-sans leading-relaxed"
          >
            Numbers don't lie. Here's how my freelance writing expertise 
            has helped businesses achieve measurable growth and success.
          </motion.p>
          
          <motion.button
            variants={fadeInUp}
            whileTap={{ scale: 0.95 }}
            className="bg-gray-900 text-white px-10 py-5 text-base font-normal font-display tracking-wider transition-all duration-300 hover:bg-gray-800 shadow-xl hover:shadow-2xl"
          >
            GET STARTED TODAY
          </motion.button>
        </motion.div>

        {/* Enhanced Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {stats.map((stat, index) => {
            const IconComponent = stat.icon as React.ComponentType<{ className?: string }>;
            const colors = getStatColors(stat.color);
            
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 50, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ 
                  delay: index * 0.15, 
                  duration: 0.6,
                  ease: "easeOut"
                }}
                whileHover={{ 
                  y: -8, 
                  scale: 1.02,
                  transition: { duration: 0.3 }
                }}
                className={`bg-white/90 backdrop-blur-sm p-8 border-2 ${colors.border} hover:border-white/50 shadow-lg hover:shadow-2xl transition-all duration-300 group relative overflow-hidden`}
              >
                {/* Background Pattern */}
                <div className={`absolute top-0 right-0 w-20 h-20 ${colors.bg} opacity-50 transform rotate-45 translate-x-10 -translate-y-10 group-hover:scale-125 transition-transform duration-300`}></div>
                
                {/* Icon */}
                <motion.div
                  whileHover={{ 
                    rotate: 10,
                    scale: 1.1,
                    transition: { duration: 0.3 }
                  }}
                  className="flex justify-center mb-6"
                >
                  <div className={`w-16 h-16 ${colors.bg} rounded-full flex items-center justify-center group-hover:shadow-lg transition-shadow duration-300`}>
                    <IconComponent className={`text-2xl ${colors.icon}`} />
                  </div>
                </motion.div>

                {/* Number */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: index * 0.15 + 0.3, duration: 0.5, type: "spring" }}
                  className="text-4xl md:text-5xl font-bold text-gray-900 mb-3 font-display relative"
                >
                  {stat.number}
                  {/* Accent Line */}
                  <div className={`absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-1 ${colors.accent} opacity-60 group-hover:w-12 transition-all duration-300`}></div>
                </motion.div>

                {/* Label */}
                <h3 className="text-lg font-semibold text-gray-800 mb-3 font-display tracking-wide group-hover:text-gray-900 transition-colors duration-300">
                  {stat.label}
                </h3>

                {/* Description */}
                <p className="text-sm text-gray-600 font-sans leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                  {stat.description}
                </p>

                {/* Hover Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </motion.div>
            );
          })}
        </div>

       
      </div>
    </section>
  );
};

const ResumeSection: React.FC = () => {
  const { ref, controls } = useScrollAnimation();
  const [detailLevel, setDetailLevel] = useState<'basic' | 'detailed'>('basic');
  const [downloadState, setDownloadState] = useState<'idle' | 'downloading' | 'success'>('idle');

  const handleDownloadPDF = async () => {
    setDownloadState('downloading');
    
    // Create a mock PDF blob (in a real app, you'd fetch from your server)
    const createMockPDF = () => {
      const content = `
Alexandra Wilson - Freelance Writer & Content Strategist
Email: alex@example.com | Phone: (555) 123-4567 | Location: San Francisco, CA

PROFESSIONAL SUMMARY
Experienced freelance writer and content strategist with 5+ years of expertise in technical writing, content strategy, and copywriting. Proven track record of helping startups and enterprises improve their communication and drive growth through strategic content.

CORE EXPERTISE
• Technical Writing & Documentation
• Content Strategy & Planning  
• Creative Copywriting
• SEO Optimization
• Marketing Copy
• Brand Storytelling

EXPERIENCE
Freelance Writer & Content Strategist (2019 - Present)
• Completed 100+ projects for 50+ clients
• Specializing in technical documentation, marketing copy, and content strategy
• 95% client satisfaction rate with strong repeat business

Senior Content Writer, Tech Corp (2017 - 2019)
• Led content strategy for major product launches
• Increased user engagement by 40% through improved documentation
• Collaborated with cross-functional teams on content planning

EDUCATION
Bachelor of Arts in English Literature
University of California, Berkeley (2015 - 2017)

CERTIFICATIONS
• Google Analytics Certified
• HubSpot Content Marketing Certified
• Technical Writing Certificate

SKILLS
Writing: Technical Writing, Copywriting, Content Strategy, SEO Writing
Technical: Google Analytics, HubSpot, WordPress, Figma
Marketing: Email Marketing, Social Media, Brand Strategy
Tools: Notion, Slack, Trello, Adobe Creative Suite
      `;
      
      return new Blob([content], { type: 'text/plain' });
    };

    try {
      // Simulate download delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const blob = createMockPDF();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Alexandra_Wilson_Resume.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setDownloadState('success');
      setTimeout(() => setDownloadState('idle'), 3000);
    } catch (error) {
      setDownloadState('idle');
    }
  };

  const getDownloadButtonContent = () => {
    switch (downloadState) {
      case 'downloading':
        return (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            DOWNLOADING...
          </>
        );
      case 'success':
        return (
          <>
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            DOWNLOADED!
          </>
        );
      default:
        return (
          <>
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            DOWNLOAD PDF
          </>
        );
    }
  };

  return (
    <section id="resume" className="py-24 md:py-32 bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-r from-purple-200 to-blue-200 rounded-full mix-blend-multiply filter blur-2xl animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-gradient-to-r from-yellow-200 to-orange-200 rounded-full mix-blend-multiply filter blur-2xl animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <motion.div
          ref={ref}
          initial="initial"
          animate={controls}
          variants={staggerContainer}
          className="text-center mb-20"
        >
          {/* Section Badge */}
          <motion.div
            variants={fadeInUp}
            className="inline-flex items-center gap-2 bg-white px-4 py-2 text-sm text-gray-600 font-display tracking-wider uppercase mb-8 shadow-sm border border-gray-100"
          >
            <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
            Professional Background
          </motion.div>

          <motion.h2
            variants={fadeInUp}
            className="text-4xl md:text-5xl font-normal text-gray-900 mb-8 font-display tracking-wide"
          >
            Resume & Experience
          </motion.h2>
          
          <motion.p
            variants={fadeInUp}
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-sans mb-12"
          >
            Over 5 years of proven success in freelance writing, content strategy, 
            and technical communication across diverse industries.
          </motion.p>

          {/* Enhanced Toggle for detail levels */}
          <motion.div
            variants={fadeInUp}
            className="inline-flex bg-white border-2 border-gray-200 shadow-lg mb-16 overflow-hidden"
          >
            <motion.button
              onClick={() => setDetailLevel('basic')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`px-8 py-4 text-sm font-display tracking-wider transition-all duration-300 ${
                detailLevel === 'basic' 
                  ? 'bg-yellow-400 text-gray-900 shadow-md' 
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              QUICK OVERVIEW
            </motion.button>
            <motion.button
              onClick={() => setDetailLevel('detailed')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`px-8 py-4 text-sm font-display tracking-wider transition-all duration-300 ${
                detailLevel === 'detailed' 
                  ? 'bg-yellow-400 text-gray-900 shadow-md' 
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              DETAILED VIEW
            </motion.button>
          </motion.div>

          {/* Key Stats */}
          <motion.div
            variants={fadeInUp}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto mb-16"
          >
            {[
              { number: '5+', label: 'Years Experience' },
              { number: '100+', label: 'Projects Completed' },
              { number: '50+', label: 'Happy Clients' },
              { number: '95%', label: 'Client Retention' }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + (index * 0.1) }}
                className="text-center bg-white/80 backdrop-blur-sm p-4 border border-gray-200 shadow-sm"
              >
                <div className="text-2xl font-bold text-gray-900 font-display mb-1">
                  {stat.number}
                </div>
                <div className="text-sm text-gray-600 font-sans">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
          {/* Enhanced Contact Info Section */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-1"
          >
            {/* Professional Profile Card */}
            <div className="bg-white border border-gray-200 shadow-lg p-8 mb-8 relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-yellow-100 to-transparent opacity-50"></div>
              
              <div className="relative z-10">
                <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center mb-6 mx-auto shadow-lg">
                  <span className="text-white text-2xl font-bold font-display">
                    {resumeData.basic.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>

                <div className="text-center mb-6">
                  <h3 className="text-xl font-semibold text-gray-900 font-display mb-2">
                    {resumeData.basic.name}
                  </h3>
                  <p className="text-emerald-600 font-display tracking-wide text-sm mb-4">
                    {resumeData.basic.title}
                  </p>
                </div>

                <div className="space-y-3 text-sm font-sans">
                  <div className="flex items-center gap-3 p-2 hover:bg-gray-50 transition-colors duration-200">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <span className="text-gray-700">{resumeData.basic.email}</span>
                  </div>

                  <div className="flex items-center gap-3 p-2 hover:bg-gray-50 transition-colors duration-200">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <span className="text-gray-700">{resumeData.basic.phone}</span>
                  </div>

                  <div className="flex items-center gap-3 p-2 hover:bg-gray-50 transition-colors duration-200">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <span className="text-gray-700">{resumeData.basic.location}</span>
                  </div>

                  <div className="flex items-center gap-3 p-2 hover:bg-gray-50 transition-colors duration-200">
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9m0 9c-5 0-9-4-9-9s4-9 9-9" />
                      </svg>
                    </div>
                    <span className="text-gray-700">{resumeData.basic.website}</span>
                  </div>
                </div>

                {/* Download Resume Button */}
                <motion.button
                  onClick={handleDownloadPDF}
                  disabled={downloadState === 'downloading'}
                  whileHover={downloadState === 'idle' ? { scale: 1.02, y: -2 } : {}}
                  whileTap={downloadState === 'idle' ? { scale: 0.98 } : {}}
                  className={`w-full py-3 px-6 mt-8 font-display tracking-wider text-sm transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center ${
                    downloadState === 'downloading' 
                      ? 'bg-gray-600 text-white cursor-not-allowed' 
                      : downloadState === 'success'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  {getDownloadButtonContent()}
                </motion.button>
              </div>
            </div>

            {/* Skills Overview */}
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200 p-6 shadow-sm">
              <h4 className="text-lg font-semibold text-gray-900 font-display mb-4 flex items-center gap-2">
                <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                Core Expertise
              </h4>
              <div className="flex flex-wrap gap-2">
                {['Technical Writing', 'Content Strategy', 'Copywriting', 'SEO Optimization', 'Documentation', 'Marketing Copy'].map((skill) => (
                  <span
                    key={skill}
                    className="bg-white text-gray-700 px-3 py-1 text-xs font-display tracking-wider border border-gray-200 shadow-sm"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Enhanced Experience & Education Section */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-2"
          >
            {/* Experience Section */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-normal text-gray-900 font-display tracking-wide">
                  Professional Experience
                </h3>
              </div>
              
              <div className="space-y-8">
                {resumeData.experience.map((exp, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.2 }}
                    className="bg-white border border-gray-200 p-8 shadow-sm hover:shadow-md transition-all duration-300 relative overflow-hidden group"
                  >
                    {/* Background accent */}
                    <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-yellow-400 to-orange-400"></div>
                    
                    <div className="relative">
                      <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4 gap-4">
                        <div className="flex-1">
                          <h4 className="text-xl font-semibold text-gray-900 font-display tracking-wide mb-1">
                            {exp.title}
                          </h4>
                          <p className="text-blue-600 font-semibold font-sans text-base mb-2">
                            {exp.company}
                          </p>
                        </div>
                        <div className="bg-yellow-100 text-yellow-800 px-4 py-2 text-sm font-display tracking-wider border border-yellow-200 shadow-sm">
                          {exp.period}
                        </div>
                      </div>
                      
                      <p className="text-gray-700 leading-relaxed mb-4 font-sans">
                        {exp.description}
                      </p>
                      
                      {detailLevel === 'detailed' && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          transition={{ duration: 0.3 }}
                          className="space-y-2"
                        >
                          <h5 className="text-sm font-semibold text-gray-800 font-display tracking-wide mb-3">
                            Key Achievements:
                          </h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {exp.achievements.map((achievement, i) => (
                              <div
                                key={i}
                                className="flex items-start gap-3 bg-emerald-50 p-3 border border-emerald-100"
                              >
                                <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                                <span className="text-sm text-gray-700 font-sans">
                                  {achievement}
                                </span>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </div>

                    {/* Hover effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-yellow-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Education & Certifications */}
            {detailLevel === 'detailed' && (
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-8"
              >
                {/* Education */}
                <div className="bg-white border border-gray-200 p-8 shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 font-display">
                      Education
                    </h3>
                  </div>
                  
                  <div className="space-y-4">
                    {resumeData.education.map((edu, index) => (
                      <div key={index} className="border-l-2 border-purple-200 pl-4 py-2">
                        <h4 className="font-semibold text-gray-900 font-display text-base">
                          {edu.degree}
                        </h4>
                        <p className="text-gray-600 font-sans text-sm">
                          {edu.school}
                        </p>
                        <p className="text-purple-600 font-display text-xs tracking-wider">
                          {edu.year}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Certifications */}
                <div className="bg-white border border-gray-200 p-8 shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 font-display">
                      Certifications
                    </h3>
                  </div>
                  
                  <div className="space-y-3">
                    {resumeData.certifications.map((cert, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-100 hover:bg-emerald-100 transition-colors duration-200">
                        <div className="w-2 h-2 bg-emerald-400 rounded-full flex-shrink-0"></div>
                        <span className="text-sm text-gray-700 font-sans">
                          {cert}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>

     
      </div>
    </section>
  );
};

const Testimonials: React.FC = () => {
  const { ref, controls } = useScrollAnimation();

  return (
    <section id="testimonials" className="py-24 md:py-32 bg-gradient-to-br from-white via-gray-50 to-blue-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-20 w-72 h-72 bg-gradient-to-r from-emerald-200 to-blue-200 rounded-full mix-blend-multiply filter blur-2xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-yellow-200 to-purple-200 rounded-full mix-blend-multiply filter blur-2xl animate-pulse" style={{ animationDelay: '3s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <motion.div
          ref={ref}
          initial="initial"
          animate={controls}
          variants={staggerContainer}
          className="text-center mb-20"
        >
          {/* Section Badge */}
          <motion.div
            variants={fadeInUp}
            className="inline-flex items-center gap-2 bg-white px-4 py-2 text-sm text-gray-600 font-display tracking-wider uppercase mb-8 shadow-sm border border-gray-100"
          >
            <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
            Client Success Stories
          </motion.div>

          <motion.h2
            variants={fadeInUp}
            className="text-4xl md:text-5xl font-normal text-gray-900 mb-8 font-display tracking-wide leading-tight"
          >
            What Clients Say
          </motion.h2>
          
          <motion.p
            variants={fadeInUp}
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-sans mb-12"
          >
            Don't just take my word for it. Here's what industry leaders have to say 
            about working with me as their freelance writer.
          </motion.p>

          {/* Overall Stats */}
          <motion.div
            variants={fadeInUp}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl mx-auto mb-16"
          >
            {[
              { 
                number: '5.0', 
                label: 'Average Rating', 
                icon: (
                  <svg className="w-6 h-6 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                )
              },
              { 
                number: '95%', 
                label: 'Satisfaction Rate', 
                icon: (
                  <svg className="w-6 h-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )
              },
              { 
                number: '48hr', 
                label: 'Response Time', 
                icon: (
                  <svg className="w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                )
              },
              { 
                number: '100%', 
                label: 'Would Recommend', 
                icon: (
                  <svg className="w-6 h-6 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                  </svg>
                )
              }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + (index * 0.1) }}
                className="text-center bg-white/80 backdrop-blur-sm p-4 border border-gray-200 shadow-sm"
              >
                <div className="flex justify-center mb-2">{stat.icon}</div>
                <div className="text-xl font-bold text-gray-900 font-display mb-1">
                  {stat.number}
                </div>
                <div className="text-xs text-gray-600 font-sans">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Enhanced Testimonials Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: index * 0.15, duration: 0.6 }}
              whileHover={{ 
                y: -8,
                transition: { duration: 0.3 }
              }}
              className="bg-white border border-gray-200 shadow-lg hover:shadow-2xl transition-all duration-300 group relative overflow-hidden"
            >
              {/* Quote Icon Background */}
              <div className="absolute top-6 right-6 w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center opacity-60 group-hover:opacity-100 transition-opacity duration-300">
                <svg className="w-6 h-6 text-emerald-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z"/>
                </svg>
              </div>

              <div className="p-8 relative z-10">
                {/* Star Rating */}
                <div className="flex items-center gap-1 mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <motion.span 
                      key={i}
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.15 + (i * 0.05) + 0.5 }}
                      className="text-yellow-400 text-lg"
                    >
                      ★
                    </motion.span>
                  ))}
                  <span className="ml-2 text-sm text-gray-500 font-display">5.0</span>
                </div>
                
                {/* Testimonial Content */}
                <blockquote className="text-gray-700 leading-relaxed mb-8 font-sans text-base italic relative">
                  <span className="text-4xl text-emerald-200 absolute -top-2 -left-2">"</span>
                  <span className="relative z-10">{testimonial.content}</span>
                  <span className="text-4xl text-emerald-200 absolute -bottom-4 -right-2">"</span>
                </blockquote>
                
                {/* Author Info */}
                <div className="flex items-center gap-4 pt-6 border-t border-gray-100">
                  <div className="relative">
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-md"
                    />
                    {/* Online indicator */}
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-400 rounded-full border-2 border-white"></div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-gray-900 font-display tracking-wide mb-1">
                      {testimonial.name}
                    </h4>
                    <p className="text-sm text-gray-600 font-sans">
                      {testimonial.role}
                    </p>
                    <p className="text-sm text-blue-600 font-semibold font-sans">
                      {testimonial.company}
                    </p>
                  </div>
                  {/* Company Badge */}
                  <div className="bg-gray-100 px-3 py-1 text-xs text-gray-600 font-display tracking-wider uppercase">
                    Verified
                  </div>
                </div>
              </div>

              {/* Hover Effect Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 to-blue-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              
              {/* Border Accent on Hover */}
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-emerald-200 transition-all duration-300" />
            </motion.div>
          ))}
        </div>

      
      </div>
    </section>
  );
};

// Adaptive Contact Chips Component
const ContactChips: React.FC = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const contactMethods = [
    { 
      icon: (
        <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ), 
      label: 'Email', 
      value: 'alexandra@example.com', 
      href: 'mailto:alexandra@example.com',
      color: theme.colors.blue[500] 
    },
    { 
      icon: (
        <svg className="w-5 h-5 text-blue-700" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      ), 
      label: 'LinkedIn', 
      value: '/in/alexandra-writer', 
      color: theme.colors.blue[600] 
    },
    { 
      icon: (
        <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      ), 
      label: 'Phone', 
      value: '+1 (555) 123-4567', 
      href: 'tel:+15551234567',
      color: theme.colors.emerald[500] 
    },
    { 
      icon: (
        <svg className="w-5 h-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9m0 9c-5 0-9-4-9-9s4-9 9-9" />
        </svg>
      ), 
      label: 'Website', 
      value: 'alexandra-writer.com', 
      href: 'https://alexandra-writer.com',
      color: theme.colors.yellow[500] 
    },
  ];

  if (isMobile) {
    // Mobile: Compact chips in a grid
  return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: theme.spacing[3],
        marginBottom: theme.spacing[8],
      }}>
        {contactMethods.map((contact, index) => (
          <motion.a
            key={contact.label}
            href={contact.href}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: theme.spacing[2],
              padding: theme.spacing[4],
              background: 'white',
              border: `1px solid ${theme.colors.primary[200]}`,
              borderRadius: '0px',
              textDecoration: 'none',
              color: 'inherit',
              textAlign: 'center',
              transition: 'all 0.3s ease',
            }}
          >
            <div style={{
              background: contact.color + '20',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
            }}>
              {contact.icon}
            </div>
            <div style={{
              fontSize: theme.typography.fontSize.xs,
              color: theme.colors.primary[600],
              fontFamily: theme.typography.fontFamily.display,
            }}>
              {contact.label}
            </div>
          </motion.a>
        ))}
      </div>
    );
  }

  // Desktop: Horizontal chips with full info
  return (
        <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing[4],
              marginBottom: theme.spacing[8],
      justifyContent: 'center',
    }}>
      {contactMethods.map((contact, index) => (
                <motion.a
                  key={contact.label}
                  href={contact.href}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
          whileHover={{ 
            scale: 1.02,
            boxShadow: theme.shadows.lg,
          }}
          whileTap={{ scale: 0.98 }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
            gap: theme.spacing[3],
            padding: `${theme.spacing[3]} ${theme.spacing[6]}`,
            background: 'white',
            border: `1px solid ${theme.colors.primary[200]}`,
            borderRadius: '0px',
                    textDecoration: 'none',
                    color: 'inherit',
                    transition: 'all 0.3s ease',
            minWidth: '200px',
                  }}
                >
                  <div style={{
            background: contact.color + '20',
            width: '40px',
            height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
            borderRadius: '50%',
                  }}>
                    {contact.icon}
                  </div>
                  <div>
                    <div style={{
                      fontSize: theme.typography.fontSize.sm,
                      color: theme.colors.primary[500],
              fontFamily: theme.typography.fontFamily.display,
              letterSpacing: '0.02em',
                    }}>
                      {contact.label}
                    </div>
                    <div style={{
              fontSize: theme.typography.fontSize.sm,
              fontWeight: 400,
                      color: theme.colors.primary[900],
                      fontFamily: theme.typography.fontFamily.sans,
                    }}>
                      {contact.value}
                    </div>
                  </div>
                </motion.a>
              ))}
            </div>
  );
};

const CTASection: React.FC = () => {
  const { ref, controls } = useScrollAnimation();

  const handleStartProject = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleBookConsultation = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
      {/* Enhanced CTA Section */}
      <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-8 md:p-12 lg:p-16 relative overflow-hidden shadow-2xl">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-400 rounded-full filter blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-400 rounded-full filter blur-3xl"></div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-400 via-blue-400 to-emerald-400"></div>
            
            <div className="relative z-10 text-center max-w-4xl mx-auto">
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.0 }}
                className="inline-flex items-center gap-2 bg-yellow-400 text-gray-900 px-4 py-2 text-sm font-display tracking-wider uppercase mb-8 shadow-lg"
              >
                <div className="w-2 h-2 bg-gray-900 rounded-full"></div>
                Limited Availability
              </motion.div>

              {/* Main Headline */}
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1 }}
                className="text-3xl md:text-4xl lg:text-5xl font-normal text-white mb-6 font-display tracking-wide leading-tight"
              >
                Ready to Transform Your 
                <span className="relative ml-3">
                  Content Strategy?
                  <div className="absolute -bottom-2 left-0 right-0 h-3 bg-yellow-400 opacity-60"></div>
                </span>
              </motion.h3>

              {/* Subtitle */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
                className="text-xl text-gray-300 mb-8 font-sans leading-relaxed max-w-2xl mx-auto"
              >
                Join 50+ companies who've seen measurable results from strategic content writing. 
                Let's discuss your project and create content that actually converts.
              </motion.p>

              {/* Value Props */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3 }}
                className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10 max-w-3xl mx-auto"
              >
                {[
                  { 
                    icon: (
                      <svg className="w-6 h-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    ),
                    title: "Fast Turnaround", 
                    desc: "Most projects completed within 1-2 weeks" 
                  },
                  { 
                    icon: (
                      <svg className="w-6 h-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ),
                    title: "Proven Results", 
                    desc: "95% client satisfaction rate" 
                  },
                  { 
                    icon: (
                      <svg className="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    ),
                    title: "Fair Pricing", 
                    desc: "No agency overhead costs" 
                  }
                ].map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.4 + (index * 0.1) }}
                    className="text-center bg-white/5 backdrop-blur-sm p-6 border border-white/10 hover:bg-white/10 transition-all duration-300"
                  >
                    <div className="flex justify-center mb-3">{item.icon}</div>
                    <h4 className="text-white font-semibold font-display text-lg mb-2">{item.title}</h4>
                    <p className="text-gray-400 font-sans text-sm">{item.desc}</p>
                  </motion.div>
                ))}
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.7 }}
                className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8"
              >
                <motion.button
                  onClick={handleStartProject}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-4 text-base font-semibold font-display tracking-wider transition-all duration-300 shadow-xl hover:shadow-2xl min-w-[200px]"
                >
                  START YOUR PROJECT
                </motion.button>
                
                <motion.button
                  onClick={handleBookConsultation}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 text-base font-semibold font-display tracking-wider transition-all duration-300 min-w-[200px]"
                >
                  BOOK FREE CONSULTATION
                </motion.button>
              </motion.div>

              {/* Trust Indicators */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.8 }}
                className="flex flex-wrap justify-center items-center gap-8 pt-8 border-t border-white/20"
              >
                <div className="flex items-center gap-2 text-gray-400 text-sm font-sans">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Currently Available
                </div>
                <div className="flex items-center gap-2 text-gray-400 text-sm font-sans">
                  <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  24hr Response Time
                </div>
                <div className="flex items-center gap-2 text-gray-400 text-sm font-sans">
                  <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  5-Star Reviews
                </div>
                <div className="flex items-center gap-2 text-gray-400 text-sm font-sans">
                  <svg className="w-4 h-4 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  100% Secure
                </div>
              </motion.div>

              {/* Urgency Note */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.9 }}
                className="mt-6 text-center"
              >
                <p className="text-yellow-300 text-sm font-sans italic">
                  ⚡ Limited spots available this month - Book your consultation today
                </p>
              </motion.div>
            </div>
          </div>
        </motion.div>
    </section>
  );
};

const Contact: React.FC = () => {
  const { ref, controls } = useScrollAnimation();
  const [formState, setFormState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    projectType: '',
    budget: '',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormState('loading');
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2500));
      
      // Simulate random success/error for demo
      if (Math.random() > 0.15) {
        setFormState('success');
        // Reset form after success
        setFormData({
          name: '',
          email: '',
          projectType: '',
          budget: '',
          message: ''
        });
        
        // Reset to idle after 4 seconds
        setTimeout(() => setFormState('idle'), 4000);
      } else {
        setFormState('error');
        setTimeout(() => setFormState('idle'), 4000);
      }
    } catch (error) {
      setFormState('error');
      setTimeout(() => setFormState('idle'), 4000);
    }
  };

  const getFormButtonContent = () => {
    switch (formState) {
      case 'loading':
        return (
          <>
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            SENDING MESSAGE...
          </>
        );
      case 'success':
        return (
          <>
            <svg className="w-5 h-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            MESSAGE SENT!
          </>
        );
      case 'error':
        return (
          <>
            <svg className="w-5 h-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            FAILED - TRY AGAIN
          </>
        );
      default:
        return (
          <>
            <svg className="w-5 h-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
            SEND MESSAGE
          </>
        );
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-r from-blue-200 to-purple-200 rounded-full mix-blend-multiply filter blur-2xl animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-gradient-to-r from-yellow-200 to-emerald-200 rounded-full mix-blend-multiply filter blur-2xl animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <motion.div
          ref={ref}
          initial="initial"
          animate={controls}
          variants={staggerContainer}
          className="text-center mb-20"
        >
          {/* Section Badge */}
          <motion.div
            variants={fadeInUp}
            className="inline-flex items-center gap-2 bg-white px-4 py-2 text-sm text-gray-600 font-display tracking-wider uppercase mb-8 shadow-sm border border-gray-100"
          >
            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
            Get In Touch
          </motion.div>

          <motion.h2
            variants={fadeInUp}
            className="text-4xl md:text-5xl font-normal text-gray-900 mb-8 font-display tracking-wide leading-tight"
          >
            Let's Work Together
          </motion.h2>
          
          <motion.p
            variants={fadeInUp}
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-sans mb-12"
          >
            Ready to transform your content strategy? Let's discuss how I can help 
            your company create content that converts and drives real business results.
          </motion.p>

          {/* Enhanced Stats Row */}
          <motion.div
            variants={fadeInUp}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto mb-16"
          >
            {[
              { 
                icon: (
                  <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
                number: '24hr', 
                label: 'Quick Response' 
              },
              { 
                icon: (
                  <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
                number: 'Free', 
                label: 'Consultation' 
              },
              { 
                icon: (
                  <svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                ),
                number: 'Fast', 
                label: 'Turnaround' 
              },
              { 
                icon: (
                  <svg className="w-5 h-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ),
                number: '5.0', 
                label: 'Rating' 
              }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + (index * 0.1) }}
                className="text-center bg-white/80 backdrop-blur-sm p-4 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300"
              >
                <div className="flex justify-center mb-2">{stat.icon}</div>
                <div className="text-lg font-bold text-gray-900 font-display mb-1">
                  {stat.number}
                </div>
                <div className="text-xs text-gray-600 font-sans">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Enhanced Contact Chips */}
          <ContactChips />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left Side - Enhanced Form */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white p-8 md:p-10 border border-gray-200 shadow-xl relative overflow-hidden"
          >
            {/* Decorative Elements */}
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-400 via-purple-400 to-yellow-400"></div>
            <div className="absolute -top-4 -right-4 w-20 h-20 bg-yellow-100 rounded-full opacity-60"></div>

            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-normal text-gray-900 font-display tracking-wide">
                  Send a Message
                </h3>
              </div>
              
              <form onSubmit={handleFormSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3 font-display tracking-wide">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      disabled={formState === 'loading'}
                      className={`w-full p-4 border-2 focus:ring-0 outline-none transition-all duration-300 font-sans text-gray-900 placeholder-gray-500 ${
                        formState === 'loading' 
                          ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
                          : 'border-gray-200 focus:border-blue-400 hover:border-gray-300 bg-white'
                      }`}
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3 font-display tracking-wide">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      disabled={formState === 'loading'}
                      className={`w-full p-4 border-2 focus:ring-0 outline-none transition-all duration-300 font-sans text-gray-900 placeholder-gray-500 ${
                        formState === 'loading' 
                          ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
                          : 'border-gray-200 focus:border-blue-400 hover:border-gray-300 bg-white'
                      }`}
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3 font-display tracking-wide">
                    Project Type
                  </label>
                  <select 
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleInputChange}
                    disabled={formState === 'loading'}
                    className={`w-full p-4 border-2 focus:ring-0 outline-none transition-all duration-300 font-sans text-gray-900 ${
                      formState === 'loading' 
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
                        : 'border-gray-200 focus:border-blue-400 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <option value="">Select project type</option>
                    <option value="technical-writing">Technical Writing</option>
                    <option value="content-strategy">Content Strategy</option>
                    <option value="api-documentation">API Documentation</option>
                    <option value="copywriting">Copywriting</option>
                    <option value="blog-writing">Blog Writing</option>
                    <option value="ux-writing">UX Writing</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3 font-display tracking-wide">
                    Budget Range
                  </label>
                  <select 
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    disabled={formState === 'loading'}
                    className={`w-full p-4 border-2 focus:ring-0 outline-none transition-all duration-300 font-sans text-gray-900 ${
                      formState === 'loading' 
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
                        : 'border-gray-200 focus:border-blue-400 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <option value="">Select budget range</option>
                    <option value="1000-2500">$1,000 - $2,500</option>
                    <option value="2500-5000">$2,500 - $5,000</option>
                    <option value="5000-10000">$5,000 - $10,000</option>
                    <option value="10000+">$10,000+</option>
                    <option value="discuss">Let's discuss</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3 font-display tracking-wide">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    disabled={formState === 'loading'}
                    className={`w-full p-4 border-2 focus:ring-0 outline-none transition-all duration-300 font-sans text-gray-900 placeholder-gray-500 resize-vertical ${
                      formState === 'loading' 
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
                        : 'border-gray-200 focus:border-blue-400 hover:border-gray-300 bg-white'
                    }`}
                    placeholder="Tell me about your project goals, timeline, and any specific requirements..."
                  />
                </div>
                
                <motion.button
                  whileHover={formState === 'idle' ? { scale: 1.02, y: -2 } : {}}
                  whileTap={formState === 'idle' ? { scale: 0.98 } : {}}
                  type="submit"
                  disabled={formState === 'loading'}
                  className={`w-full py-4 px-8 text-base font-normal font-display tracking-wider transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center min-h-[60px] ${
                    formState === 'loading'
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : formState === 'success'
                      ? 'bg-green-600 text-white'
                      : formState === 'error'
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-600 hover:to-yellow-700 text-white'
                  }`}
                >
                  {getFormButtonContent()}
                </motion.button>

                {/* Form Footer / Success Message */}
                {formState === 'success' ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="pt-6 border-t border-green-200 text-center bg-green-50 p-6 -mx-2"
                  >
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm font-semibold text-green-800 font-display tracking-wide">
                        MESSAGE SENT SUCCESSFULLY!
                      </p>
                    </div>
                    <p className="text-sm text-green-700 font-sans">
                      Thank you for reaching out! I'll review your message and get back to you within 24 hours.
                    </p>
                  </motion.div>
                ) : formState === 'error' ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="pt-6 border-t border-red-200 text-center bg-red-50 p-6 -mx-2"
                  >
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <p className="text-sm font-semibold text-red-800 font-display tracking-wide">
                        FAILED TO SEND MESSAGE
                      </p>
                    </div>
                    <p className="text-sm text-red-700 font-sans">
                      Sorry, there was an issue sending your message. Please try again or contact me directly at alexandra@example.com
                    </p>
                  </motion.div>
                ) : (
                <div className="pt-6 border-t border-gray-100 text-center">
                  <p className="text-sm text-gray-500 font-sans">
                    I'll get back to you within 24 hours. Your information is kept confidential.
                  </p>
                </div>
                )}
              </form>
            </div>
          </motion.div>

          {/* Right Side - Enhanced Info */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Why Choose Me */}
            <div className="bg-white p-8 border border-gray-200 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 font-display tracking-wide">
                  Why Work With Me?
                </h4>
              </div>
              
              <div className="space-y-4">
                {[
                  { title: 'Freelance Focus', desc: 'Direct collaboration without agency overhead' },
                  { title: 'Proven Results', desc: '95% client satisfaction, measurable outcomes' },
                  { title: 'Quick Turnaround', desc: '48hr response, flexible scheduling' },
                  { title: 'Expert Knowledge', desc: '5+ years in technical and content writing' }
                ].map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + (index * 0.1) }}
                    className="flex items-start gap-3 p-3 bg-gray-50 hover:bg-emerald-50 transition-colors duration-200 border border-transparent hover:border-emerald-100"
                  >
                    <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h5 className="font-semibold text-gray-900 font-display text-sm mb-1">
                        {item.title}
                      </h5>
                      <p className="text-sm text-gray-600 font-sans">
                        {item.desc}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Frequently Asked */}
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-8 border border-yellow-200 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 font-display tracking-wide">
                  Quick Questions?
                </h4>
              </div>
              
              <div className="space-y-3 text-sm">
                <div className="border-l-4 border-yellow-400 pl-4">
                  <p className="font-semibold text-gray-900 font-display mb-1">What's your typical turnaround?</p>
                  <p className="text-gray-700 font-sans">Most projects: 1-2 weeks. Rush jobs available.</p>
                </div>
                <div className="border-l-4 border-yellow-400 pl-4">
                  <p className="font-semibold text-gray-900 font-display mb-1">Do you offer revisions?</p>
                  <p className="text-gray-700 font-sans">Yes, 2 rounds included. Additional revisions available.</p>
                </div>
                <div className="border-l-4 border-yellow-400 pl-4">
                  <p className="font-semibold text-gray-900 font-display mb-1">How do you handle payments?</p>
                  <p className="text-gray-700 font-sans">50% upfront, 50% on completion. Multiple payment options.</p>
                </div>
              </div>
            </div>

            {/* Availability */}
            <div className="bg-white p-6 border border-gray-200 shadow-lg text-center">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 text-sm font-display tracking-wider mb-4">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                Currently Available
              </div>
              <p className="text-gray-600 font-sans text-sm">
                Accepting new projects starting this month. 
                <br />
                <span className="font-semibold text-gray-900">Book a free 30-minute consultation.</span>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Footer: React.FC = () => {
  const [email, setEmail] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [newsletterState, setNewsletterState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewsletterState('loading');
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate random success/error for demo
      if (Math.random() > 0.2) {
        setNewsletterState('success');
        setEmail('');
        
        // Reset to idle after 3 seconds
        setTimeout(() => setNewsletterState('idle'), 3000);
      } else {
        setNewsletterState('error');
        setTimeout(() => setNewsletterState('idle'), 3000);
      }
    } catch (error) {
      setNewsletterState('error');
      setTimeout(() => setNewsletterState('idle'), 3000);
    }
  };

  const getNewsletterButtonContent = () => {
    switch (newsletterState) {
      case 'loading':
  return (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            SUBSCRIBING...
          </>
        );
      case 'success':
        return (
          <>
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            SUBSCRIBED!
          </>
        );
      case 'error':
        return (
          <>
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            TRY AGAIN
          </>
        );
      default:
        return 'SUBSCRIBE';
    }
  };

  const socialLinks = [
    { 
      name: 'LinkedIn', 
      href: '#', 
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      ),
      color: 'hover:text-blue-400'
    },
    { 
      name: 'Twitter', 
      href: '#', 
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
        </svg>
      ),
      color: 'hover:text-blue-400'
    },
    { 
      name: 'GitHub', 
      href: '#', 
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
        </svg>
      ),
      color: 'hover:text-gray-300'
    },
    { 
      name: 'Email', 
      href: 'mailto:alexandra@example.com', 
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      color: 'hover:text-yellow-400'
    },
  ];

  return (
    <>
      <footer className="bg-gray-900 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-400 rounded-full filter blur-3xl"></div>
        </div>

        {/* Newsletter Section */}
        <div className=" bg-gradient-to-br from-yellow-400 via-yellow-300 to-yellow-500  relative z-10">
          <div className="max-w-7xl mx-auto px-6 md:px-8 py-16">
            <div className="text-center max-w-3xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h3 className="text-3xl md:text-4xl font-normal text-gray-900 mb-4 font-display tracking-wide">
                  Stay Updated
                </h3>
                <p className="text-lg text-gray-800 mb-8 font-sans">
                  Get writing tips, industry insights, and exclusive content delivered to your inbox monthly.
                </p>
                
                <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="flex-1 px-6 py-4 text-gray-900 placeholder-gray-600 bg-white border-2 border-transparent focus:border-gray-900 focus:ring-0 outline-none font-sans"
                    required
                  />
                  <motion.button
                    type="submit"
                    disabled={newsletterState === 'loading'}
                    whileHover={newsletterState === 'idle' ? { scale: 1.02 } : {}}
                    whileTap={newsletterState === 'idle' ? { scale: 0.98 } : {}}
                    className={`px-8 py-4 font-display tracking-wider text-sm transition-all duration-200 flex items-center justify-center min-w-[140px] ${
                      newsletterState === 'loading' 
                        ? 'bg-gray-700 text-white cursor-not-allowed' 
                        : newsletterState === 'success'
                        ? 'bg-green-600 text-white'
                        : newsletterState === 'error'
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}
                  >
                    {getNewsletterButtonContent()}
                  </motion.button>
                </form>
                
                <p className="text-sm text-gray-700 mt-4 font-sans">
                  No spam, unsubscribe at any time. Join 500+ writers and marketers.
                </p>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="relative z-10 py-20">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
              
              {/* Brand Section */}
              <div className="lg:col-span-2">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center">
                      <span className="text-gray-900 text-xl font-bold font-display">A</span>
                    </div>
          <div>
                      <h3 className="text-2xl font-normal text-white font-display tracking-wide">
                        Alexandra Johnson
            </h3>
                      <p className="text-yellow-400 font-display text-sm tracking-wide">
                        Freelance Writer & Content Strategist
                      </p>
                    </div>
          </div>
          
                  <p className="text-gray-300 leading-relaxed font-sans mb-6 max-w-md">
                    Helping tech companies create clear, compelling content that converts visitors into customers. 
                    Based in San Francisco, serving clients worldwide.
                  </p>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-8">
                    <div className="bg-gray-800/50 p-4 border border-gray-700">
                      <div className="text-2xl font-bold text-yellow-400 font-display mb-1">50+</div>
                      <div className="text-xs text-gray-400 font-sans">Projects Completed</div>
                    </div>
                    <div className="bg-gray-800/50 p-4 border border-gray-700">
                      <div className="text-2xl font-bold text-yellow-400 font-display mb-1">5+</div>
                      <div className="text-xs text-gray-400 font-sans">Years Experience</div>
                    </div>
                  </div>

                  {/* Social Links */}
                  <div className="flex gap-4">
                    {socialLinks.map((social) => (
                      <motion.a
                        key={social.name}
                        href={social.href}
                        whileHover={{ scale: 1.1, y: -2 }}
                        className={`w-10 h-10 bg-gray-800 hover:bg-gray-700 rounded-full flex items-center justify-center text-gray-400 ${social.color} transition-all duration-200 border border-gray-700 hover:border-gray-600`}
                        title={social.name}
                      >
                        {social.icon}
                      </motion.a>
                    ))}
                  </div>
                </motion.div>
              </div>

              {/* Services */}
          <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  <h4 className="text-lg font-semibold text-white mb-6 font-display tracking-wide">
              Services
            </h4>
                  <ul className="space-y-3">
                    {[
                      'Technical Writing',
                      'Content Strategy',
                      'API Documentation',
                      'Copywriting',
                      'UX Writing',
                      'Blog Writing'
                    ].map((service) => (
                      <li key={service}>
                        <a 
                          href="#services" 
                          className="text-gray-400 hover:text-yellow-400 transition-colors duration-200 font-sans text-sm block py-1"
                        >
                  {service}
                        </a>
                </li>
              ))}
            </ul>
                </motion.div>
          </div>
          
              {/* Quick Links */}
          <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <h4 className="text-lg font-semibold text-white mb-6 font-display tracking-wide">
                    Quick Links
            </h4>
                  <ul className="space-y-3">
                    {[
                      { name: 'About Me', href: '#about' },
                      { name: 'Portfolio', href: '#portfolio' },
                      { name: 'Resume', href: '#resume' },
                      { name: 'Testimonials', href: '#testimonials' },
                      { name: 'Contact', href: '#contact' },
                      { name: 'Blog', href: '#blog' }
              ].map((link) => (
                      <li key={link.name}>
                <a
                  href={link.href}
                          className="text-gray-400 hover:text-yellow-400 transition-colors duration-200 font-sans text-sm block py-1"
                >
                  {link.name}
                </a>
                      </li>
              ))}
                  </ul>
                </motion.div>
            </div>

              {/* Contact Info */}
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  <h4 className="text-lg font-semibold text-white mb-6 font-display tracking-wide">
                    Contact
                  </h4>
                  
                  <div className="space-y-4 mb-8">
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 text-yellow-400 mt-1">
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-gray-400 font-sans text-sm">Email</p>
                        <a href="mailto:alexandra@example.com" className="text-white hover:text-yellow-400 transition-colors duration-200 font-sans text-sm">
                          alexandra@example.com
                        </a>
          </div>
        </div>
        
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 text-yellow-400 mt-1">
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-gray-400 font-sans text-sm">Location</p>
                        <p className="text-white font-sans text-sm">San Francisco, CA</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 text-yellow-400 mt-1">
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-gray-400 font-sans text-sm">Response Time</p>
                        <p className="text-white font-sans text-sm">Within 24 hours</p>
                      </div>
                    </div>
                  </div>

                  {/* Availability Status */}
                  <div className="bg-green-900/30 border border-green-500/30 p-4 mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-green-400 font-display text-sm tracking-wider">AVAILABLE</span>
                    </div>
                    <p className="text-gray-300 font-sans text-xs">
                      Currently accepting new projects
                    </p>
                  </div>

                  {/* CTA Button */}
                  <motion.a
                    href="#contact"
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className="block bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-6 py-3 text-center font-display tracking-wider text-sm transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    START A PROJECT
                  </motion.a>
                </motion.div>
              </div>
            </div>

            {/* Bottom Section */}
            <div className="border-t border-gray-700 pt-8">
              <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                <div className="text-center md:text-left">
                  <p className="text-gray-400 font-sans text-sm">
                    © 2024 Alexandra Johnson. All rights reserved.
                  </p>
                  <p className="text-gray-500 font-sans text-xs mt-1">
                    Built with React, TypeScript & Framer Motion
                  </p>
                </div>

                <div className="flex flex-wrap gap-6 text-sm">
                  <a href="#" className="text-gray-400 hover:text-yellow-400 transition-colors duration-200 font-sans">
                    Privacy Policy
                  </a>
                  <a href="#" className="text-gray-400 hover:text-yellow-400 transition-colors duration-200 font-sans">
                    Terms of Service
                  </a>
                  <a href="#" className="text-gray-400 hover:text-yellow-400 transition-colors duration-200 font-sans">
                    Sitemap
                  </a>
                </div>
              </div>
            </div>
        </div>
      </div>
    </footer>

      {/* Back to Top Button */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            whileHover={{ scale: 1.1, y: -2 }}
            whileTap={{ scale: 0.9 }}
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 w-12 h-12 bg-yellow-400 hover:bg-yellow-500 text-gray-900 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 z-50 flex items-center justify-center"
            title="Back to top"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
};

const Hero: React.FC = () => {
  const handleViewWork = () => {
    document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleGetProposal = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section 
      id="hero" 
      className="min-h-screen flex items-center justify-center bg-white relative pt-[70px] pb-24"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-8 w-full">
        <div className="text-center max-w-3xl mx-auto py-16 md:py-24">
          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] }}
            className="text-4xl md:text-5xl lg:text-6xl font-normal text-gray-900 leading-tight mb-10 font-display tracking-wide"
          >
            Bettering the World with{' '}
            <span className="bg-yellow-300 px-2 inline-block">
              Words
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99], delay: 0.1 }}
            className="mb-12"
          >
            <h2 className="text-xl font-normal text-gray-700 mb-4 font-display tracking-wide">
              Copywriting & Content Strategy
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto font-sans">
              I help tech companies create clear, compelling content that converts. 
              From API documentation to growth-driven campaigns, I make complex ideas accessible.
            </p>
          </motion.div>

          {/* Expertise Points */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99], delay: 0.2 }}
            className="flex flex-wrap justify-center gap-4 md:gap-8 mb-14"
          >
            {[
              'Content Optimization',
              'Creative Brief Writing',
              'Brand Storytelling'
            ].map((item, index) => (
              <div key={item} className="flex items-center gap-2 text-sm text-gray-700 font-display tracking-wider">
                <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                {item}
              </div>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99], delay: 0.3 }}
            className="flex flex-wrap gap-4 justify-center items-center mb-20"
          >
            <motion.button
              onClick={handleViewWork}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-gray-900 text-white border-none py-4 px-8 text-base font-normal font-display tracking-wider transition-all duration-200 hover:bg-gray-800"
            >
              VIEW MY WORK
            </motion.button>
            
            <motion.button
              onClick={handleGetProposal}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-yellow-400 text-gray-900 border-none py-4 px-8 text-base font-normal font-display tracking-wider transition-all duration-200 hover:bg-yellow-500"
            >
              GET A PROPOSAL
            </motion.button>
          </motion.div>
            
          {/* Who Are You Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99], delay: 0.4 }}
            className="text-center"
          >
            <h3 className="text-2xl md:text-3xl font-normal text-gray-900 mb-10 font-display tracking-wider">
              Who are You?
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {[
                {
                  title: "EARLY ADOPTER",
                  subtitle: "Startup",
                  description: "Looking to establish your voice and connect with your audience",
                  bgColor: "bg-gray-100",
                  textColor: "text-gray-900"
                },
                {
                  title: "GROWTH",
                  subtitle: "FOCUSED",
                  description: "Ready to scale your content and optimize for conversions",
                  bgColor: "bg-gray-800",
                  textColor: "text-white"
                },
                {
                  title: "ESTABLISHED",
                  subtitle: "Enterprise",
                  description: "Refining your message and maintaining consistency across channels",
                  bgColor: "bg-yellow-400",
                  textColor: "text-gray-900"
                }
              ].map((card, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.02 }}
                  className={`${card.bgColor} ${card.textColor} p-8 min-h-[250px] flex flex-col justify-center items-center text-center cursor-pointer transition-all duration-300`}
                >
                  <div className={`w-10 h-10 rounded-full ${index === 1 ? 'bg-white text-gray-900' : 'bg-gray-900 text-white'} mb-4 flex items-center justify-center text-lg font-display`}>
                    {index + 1}
                  </div>
                  <h4 className="text-base font-normal mb-1 font-display tracking-wider">
                    {card.title}
                  </h4>
                  <div className="text-lg font-normal mb-4 font-display tracking-wider">
                    {card.subtitle}
                  </div>
                  <p className="text-sm leading-relaxed font-sans opacity-90">
                    {card.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Main App Component
const FreelanceWriterPortfolio: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = ['all', 'Technical Writing', 'Content Strategy', 'Copywriting', 'Documentation'];
  
  const filteredPortfolio = selectedCategory === 'all' 
    ? portfolioData 
    : portfolioData.filter(item => item.category === selectedCategory);

  return (
    <div style={{ 
      minHeight: '100vh', 
      fontFamily: theme.typography.fontFamily.sans,
      color: theme.colors.primary[900],
    }}>
      <Navigation />
      <Hero />
      <Services />
      <ClientLogos />
      <About />
      <WhyWorkWithCopywriter />
      <StatsSection />
      <Portfolio />
      <ResumeSection />
      <Testimonials />
      <CTASection />
      <Contact />
      <Footer />
    </div>
  );
};

export default FreelanceWriterPortfolio;

